import { useEffect, useMemo, useState } from "react";
import { PageHeader } from "../components/layout";
import { Button, Card, StatusIndicator } from "../components/ui";
import { ContextBar } from "../components/context-bar";
import { Disclosure, StateTag, type WorldState } from "../components/phase3";
import { ApiUnavailableState, EmptyState, LoadingState, NotConfiguredState } from "../components/states";
import { useAppContext } from "../hooks/useAppContext";
import {
  api,
  apiConfigured,
  ApiError,
  type PersonaChannelResult,
  type PersonaResult,
  type PersonaSimContent,
} from "../lib/api";

// ── Plain-language explanations ──────────────────────────────────────────────
function channelExplanation(c: PersonaChannelResult): string {
  if (!c.visible) return "Cannot see this channel — it is hidden for this persona.";
  const parts = ["Can see this channel"];
  parts.push(c.canSend ? "and can post messages" : "but cannot post messages");
  if (c.canCreateThreads) parts.push("can start threads");
  if (c.canModerate) parts.push("can moderate (manage messages)");
  return `${parts.join(", ")}.`;
}

function guildExplanation(p: PersonaResult): string {
  if (p.isAdministrator) return "Full Administrator — this persona bypasses every channel restriction and permission check.";
  const perms = p.guildPermissions ?? [];
  if (perms.length === 0) return "No server-wide permissions beyond the @everyone baseline.";
  const highlight = perms.filter((x) =>
    ["MANAGE_GUILD", "MANAGE_ROLES", "MANAGE_CHANNELS", "KICK_MEMBERS", "BAN_MEMBERS", "MODERATE_MEMBERS", "MENTION_EVERYONE"].includes(x),
  );
  if (highlight.length === 0) return "Ordinary member-level server permissions.";
  return `Elevated server permissions: ${highlight.join(", ")}.`;
}

// ── Presentational explorer (testable with mock data) ─────────────────────────
export function PersonaExplorer({ content }: { content: PersonaSimContent }): JSX.Element {
  const worlds = useMemo(() => {
    const w: WorldState[] = [];
    if (content.current?.length) w.push("current");
    if (content.desired?.length) w.push("desired");
    return w;
  }, [content]);

  const [world, setWorld] = useState<WorldState>(worlds[0] ?? "current");
  const personas = world === "desired" ? content.desired ?? [] : content.current ?? [];
  const [primaryId, setPrimaryId] = useState<string>(personas[0]?.personaId ?? "");
  const [compareId, setCompareId] = useState<string>("");

  // Keep selection valid when the available world/personas change.
  useEffect(() => {
    if (!worlds.includes(world) && worlds[0]) setWorld(worlds[0]);
  }, [worlds, world]);
  useEffect(() => {
    const first = personas[0];
    if (first && !personas.some((p) => p.personaId === primaryId)) setPrimaryId(first.personaId);
  }, [personas, primaryId]);

  const primary = personas.find((p) => p.personaId === primaryId) ?? personas[0];
  const compare = compareId ? personas.find((p) => p.personaId === compareId) : undefined;

  // Cross-world comparison (current vs desired) for the selected persona.
  const crossPair =
    content.current?.length && content.desired?.length && primary
      ? {
          current: content.current.find((p) => p.personaId === primary.personaId),
          desired: content.desired.find((p) => p.personaId === primary.personaId),
        }
      : null;

  if (!primary) return <EmptyState title="No personas to simulate">The simulation returned no persona results.</EmptyState>;

  return (
    <div className="simulator">
      <div className="row" style={{ gap: "0.75rem", alignItems: "flex-end", marginBottom: "1rem" }}>
        {worlds.length > 1 ? (
          <label className="field">
            <span className="field__label">State</span>
            <select className="field__input" value={world} onChange={(e) => setWorld(e.target.value as WorldState)}>
              {worlds.map((w) => (
                <option key={w} value={w}>
                  {w === "current" ? "Current — live Discord" : "Desired — blueprint"}
                </option>
              ))}
            </select>
          </label>
        ) : (
          <div>
            <StateTag state={world} />
          </div>
        )}
        <label className="field">
          <span className="field__label">Persona</span>
          <select className="field__input" value={primary.personaId} onChange={(e) => setPrimaryId(e.target.value)}>
            {personas.map((p) => (
              <option key={p.personaId} value={p.personaId}>
                {p.label}
              </option>
            ))}
          </select>
        </label>
        <label className="field">
          <span className="field__label">Compare with (optional)</span>
          <select className="field__input" value={compareId} onChange={(e) => setCompareId(e.target.value)}>
            <option value="">— none —</option>
            {personas
              .filter((p) => p.personaId !== primary.personaId)
              .map((p) => (
                <option key={p.personaId} value={p.personaId}>
                  {p.label}
                </option>
              ))}
          </select>
        </label>
      </div>

      <p className="section-note">
        Custom role combinations aren't computed server-side in this release — ODIN simulates its built-in personas.
      </p>

      <div className={compare ? "persona-compare persona-compare--two" : "persona-compare"}>
        <PersonaCard persona={primary} world={world} />
        {compare ? <PersonaCard persona={compare} world={world} /> : null}
      </div>

      {crossPair?.current && crossPair.desired ? (
        <Card title={`Current → Desired for ${primary.label}`}>
          <div className="persona-compare persona-compare--two">
            <div>
              <StateTag state="current" />
              <p className="muted">{crossPair.current.visibleChannels?.length ?? 0} visible channels</p>
            </div>
            <div>
              <StateTag state="desired" />
              <p className="muted">{crossPair.desired.visibleChannels?.length ?? 0} visible channels</p>
            </div>
          </div>
          <CrossDiff current={crossPair.current} desired={crossPair.desired} />
        </Card>
      ) : null}
    </div>
  );
}

function PersonaCard({ persona, world }: { persona: PersonaResult; world: WorldState }): JSX.Element {
  return (
    <Card title={persona.label}>
      <StateTag state={world} />
      <div style={{ marginTop: "0.5rem" }}>
        <StatusIndicator tone={persona.isAdministrator ? "danger" : "neutral"} label={persona.isAdministrator ? "Administrator" : "Standard permissions"} />
      </div>
      <p className="persona-explain">{guildExplanation(persona)}</p>
      {(persona.warnings ?? []).length > 0 ? (
        <p className="dryrun-stale">{persona.warnings.join(" ")}</p>
      ) : null}

      <h4 className="subhead">Channels ({persona.channels?.length ?? 0})</h4>
      <ul className="channel-list">
        {(persona.channels ?? []).map((c) => (
          <li key={c.channelId} className={c.visible ? "channel channel--visible" : "channel channel--hidden"}>
            <span className="channel__name">
              {c.visible ? "👁" : "🚫"} {c.name}
            </span>
            <span className="channel__explain">{channelExplanation(c)}</span>
          </li>
        ))}
      </ul>

      <Disclosure summary="Technical detail (roles & permission flags)">
        <p>
          <strong>Assigned role ids:</strong> {(persona.assignedRoleIds ?? []).join(", ") || "—"}
        </p>
        <p>
          <strong>Guild permission flags:</strong>
        </p>
        <pre className="json-view">{(persona.guildPermissions ?? []).join("\n") || "(none)"}</pre>
      </Disclosure>
    </Card>
  );
}

function CrossDiff({ current, desired }: { current: PersonaResult; desired: PersonaResult }): JSX.Element {
  const curVisible = new Set(current.visibleChannels ?? []);
  const desVisible = new Set(desired.visibleChannels ?? []);
  const names = new Map<string, string>();
  for (const c of [...(current.channels ?? []), ...(desired.channels ?? [])]) names.set(c.channelId, c.name);
  const newlyVisible = [...desVisible].filter((id) => !curVisible.has(id));
  const newlyHidden = [...curVisible].filter((id) => !desVisible.has(id));
  const nm = (id: string) => names.get(id) ?? id;
  return (
    <div className="persona-row__diffs" style={{ marginTop: "0.75rem" }}>
      <div>
        <span className="diff-label diff-label--add">Newly visible</span>
        {newlyVisible.length === 0 ? <span className="muted"> none</span> : <ul>{newlyVisible.map((id) => <li key={id}>{nm(id)}</li>)}</ul>}
      </div>
      <div>
        <span className="diff-label diff-label--remove">Newly hidden</span>
        {newlyHidden.length === 0 ? <span className="muted"> none</span> : <ul>{newlyHidden.map((id) => <li key={id}>{nm(id)}</li>)}</ul>}
      </div>
    </div>
  );
}

// ── Data-fetching container ──────────────────────────────────────────────────
export function Simulator(): JSX.Element {
  const ctx = useAppContext();
  const [snapshotId, setSnapshotId] = useState("");
  const [versionId, setVersionId] = useState("");
  const [state, setState] = useState<
    | { kind: "idle" }
    | { kind: "loading" }
    | { kind: "ready"; content: PersonaSimContent; mode: string }
    | { kind: "error"; message: string }
  >({ kind: "idle" });

  const ready = ctx.tenantId && ctx.guildId;

  const run = async () => {
    if (!ctx.tenantId || !ctx.guildId) return;
    setState({ kind: "loading" });
    try {
      const sim = await api.runPersonaSimulation(ctx.tenantId, ctx.guildId, {
        snapshotId: snapshotId.trim() || null,
        blueprintVersionId: versionId.trim() || null,
      });
      setState({ kind: "ready", content: sim.content, mode: sim.mode });
    } catch (e) {
      setState({ kind: "error", message: e instanceof ApiError ? e.message : "Could not run simulation" });
    }
  };

  return (
    <>
      <PageHeader title="Permission simulator">
        <p>
          See who can view and do what, in plain language — for the current live server, the desired blueprint, or a
          side-by-side comparison. This is read-only analysis; nothing is changed.
        </p>
      </PageHeader>

      {!apiConfigured ? (
        <NotConfiguredState title="Control API URL not configured">
          Set <code>VITE_CONTROL_API_URL</code> to run persona simulations.
        </NotConfiguredState>
      ) : (
        <>
          <ContextBar requireGuild />
          {!ready ? (
            <EmptyState title="Select a workspace and server">Choose a workspace and enter your ODIN server id above.</EmptyState>
          ) : (
            <>
              <Card
                title="Run a simulation"
                description="Provide a completed snapshot id (current state), a blueprint version id (desired state), or both to compare."
              >
                <div className="row">
                  <label className="field">
                    <span className="field__label">Snapshot id — current state</span>
                    <input className="field__input" placeholder="snapshot id (optional)" value={snapshotId} onChange={(e) => setSnapshotId(e.target.value)} aria-label="Snapshot id" />
                  </label>
                  <label className="field">
                    <span className="field__label">Blueprint version id — desired state</span>
                    <input className="field__input" placeholder="version id (optional)" value={versionId} onChange={(e) => setVersionId(e.target.value)} aria-label="Blueprint version id" />
                  </label>
                  <Button variant="primary" disabled={!snapshotId.trim() && !versionId.trim()} onClick={run}>
                    Run simulation
                  </Button>
                </div>
              </Card>

              {state.kind === "loading" ? <LoadingState label="Simulating personas…" /> : null}
              {state.kind === "error" ? <ApiUnavailableState detail={state.message} /> : null}
              {state.kind === "ready" ? <PersonaExplorer content={state.content} /> : null}
            </>
          )}
        </>
      )}
    </>
  );
}
